/*
 OK FBI — Full-Screen CCTV Control Room
 Premium surveillance workstation. Play/Pause/Scrubber/Zoom/Pan/Fullscreen,
 Camera switching, mark area of interest, capture paused frame as evidence.
 Character continuity is guaranteed because captured frames reuse the exact
 dossier portrait for the associated suspect (no generative variance).
*/
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { useAuth } from "@/pages/AuthPage";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, RotateCw, SkipBack, SkipForward, ZoomIn, ZoomOut, Maximize2, Minimize2,
  Square, Camera as CameraIcon, X, Save, Pin, Eye, EyeOff, Trash2, Radio, AlertCircle, Search,
} from "lucide-react";

// utility
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const secondsToTC = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

/**
 * Frame renderer — a canvas surface that draws the current CCTV scene
 * with realistic CRT chrome. When a suspect is present in the current event,
 * their dossier portrait is composed into the scene (character continuity).
 */
function CCTVFrameCanvas({ camera, event, seconds, suspects, zoom, pan, marker, paused }) {
  const canvasRef = useRef(null);
  const suspect = event?.suspect_id ? suspects.find(s => s.id === event.suspect_id) : null;
  const [imgTick, setImgTick] = useState(0);
  const suspectImgRef = useRef(null);

  // Preload suspect portrait for stable composition
  useEffect(() => {
    if (!suspect) { suspectImgRef.current = null; setImgTick(t => t + 1); return; }
    const img = new Image();
    img.onload = () => { suspectImgRef.current = img; setImgTick(t => t + 1); };
    img.onerror = () => { suspectImgRef.current = null; setImgTick(t => t + 1); };
    img.src = assetUrl(suspect.portrait);
  }, [suspect?.id]); // eslint-disable-line

  useEffect(() => {
    const cvs = canvasRef.current; if (!cvs) return;
    const ctx = cvs.getContext("2d");
    let raf;
    const draw = () => {
      const W = cvs.width, H = cvs.height;
      ctx.clearRect(0, 0, W, H);
      // Base gradient: dark scene
      const grad = ctx.createRadialGradient(W/2, H/2, 40, W/2, H/2, Math.max(W, H) * 0.75);
      grad.addColorStop(0, "#12140F");
      grad.addColorStop(0.6, "#08090A");
      grad.addColorStop(1, "#000");
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

      // Apply zoom/pan transform
      ctx.save();
      const cx = W / 2 + pan.x * W * 0.4;
      const cy = H / 2 + pan.y * H * 0.4;
      ctx.translate(cx, cy);
      ctx.scale(zoom, zoom);
      ctx.translate(-W / 2, -H / 2);

      // Draw scene silhouette (procedural: perspective corridor / room hint)
      drawScenery(ctx, W, H, camera);

      // Composite suspect portrait as if captured by camera
      const img = suspectImgRef.current;
      if (img && suspect) {
        const w = H * 0.55, h = w * 1.28;
        const px = W * 0.5 - w / 2;
        const py = H * 0.20;
        ctx.save();
        ctx.globalAlpha = 0.92;
        // subtle noise mask around portrait
        ctx.beginPath();
        ctx.rect(px, py, w, h);
        ctx.clip();
        ctx.drawImage(img, px, py, w, h);
        // color grade
        ctx.fillStyle = "rgba(20,26,20,0.35)";
        ctx.fillRect(px, py, w, h);
        ctx.restore();
        // subtle backlight rim
        ctx.save();
        ctx.strokeStyle = "rgba(160,180,140,0.15)";
        ctx.lineWidth = 2;
        ctx.strokeRect(px - 1, py - 1, w + 2, h + 2);
        ctx.restore();
      }

      // Scanlines
      ctx.globalAlpha = 0.09;
      for (let y = 0; y < H; y += 3) {
        ctx.fillStyle = y % 6 === 0 ? "#000" : "rgba(180,220,180,0.3)";
        ctx.fillRect(0, y, W, 1);
      }
      ctx.globalAlpha = 1;

      // Grain noise (draw noise directly, don't read pixels back)
      const noiseAmt = paused ? 0.05 : 0.12;
      ctx.save();
      ctx.globalAlpha = noiseAmt;
      ctx.fillStyle = "#c8d8b8";
      for (let i = 0; i < 260; i++) {
        const gx = Math.random() * W, gy = Math.random() * H;
        ctx.fillRect(gx, gy, 1, 1);
      }
      ctx.globalAlpha = noiseAmt * 0.6;
      ctx.fillStyle = "#000";
      for (let i = 0; i < 160; i++) {
        ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
      }
      ctx.restore();

      ctx.restore();

      // Vignette + chromatic tint (green surveillance cast)
      const vg = ctx.createRadialGradient(W/2, H/2, W*0.3, W/2, H/2, W*0.75);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.65)");
      ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);

      // Marker rect (area of interest)
      if (marker) {
        const mx = marker.x * W, my = marker.y * H, mw = marker.w * W, mh = marker.h * H;
        ctx.strokeStyle = "#C5A059";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(mx, my, mw, mh);
        ctx.setLineDash([]);
        // corner brackets
        const c = 10;
        ctx.strokeStyle = "#F0D97C";
        [[mx, my],[mx+mw-c, my],[mx, my+mh-c],[mx+mw-c, my+mh-c]].forEach(([bx, by]) => {
          ctx.beginPath(); ctx.moveTo(bx, by+c); ctx.lineTo(bx, by); ctx.lineTo(bx+c, by); ctx.stroke();
        });
      }
    };
    draw();
    // continuous minor refresh (grain) only when not paused, to keep perf sane
    if (!paused) raf = requestAnimationFrame(function loop() { draw(); raf = requestAnimationFrame(loop); });
    return () => raf && cancelAnimationFrame(raf);
  }, [camera?.id, event?.t, suspect?.id, zoom, pan.x, pan.y, marker, paused, seconds, imgTick]);

  return (
    <canvas ref={canvasRef} width={1280} height={720}
      className="w-full h-full object-contain bg-black" style={{ imageRendering: "auto" }} />
  );
}

function drawScenery(ctx, W, H, camera) {
  // Simple procedural context per camera id — a stylized silhouette
  const key = (camera?.id || "").toLowerCase();
  ctx.save();
  ctx.strokeStyle = "rgba(120,150,120,0.22)";
  ctx.lineWidth = 1;
  // perspective floor lines
  const vpx = W / 2, vpy = H * 0.55;
  ctx.beginPath();
  for (let i = -6; i <= 6; i++) {
    ctx.moveTo(W/2 + i * 60, H);
    ctx.lineTo(vpx, vpy);
  }
  // horizon
  ctx.moveTo(0, vpy); ctx.lineTo(W, vpy);
  ctx.stroke();

  // door / archway hint
  ctx.fillStyle = "rgba(60,80,60,0.12)";
  ctx.fillRect(W*0.4, H*0.25, W*0.2, H*0.35);
  ctx.strokeStyle = "rgba(200,220,180,0.22)";
  ctx.strokeRect(W*0.4, H*0.25, W*0.2, H*0.35);
  ctx.restore();
}

/** Timeline scrubber */
function Scrubber({ duration, seconds, events, onSeek, onEventClick }) {
  const barRef = useRef(null);
  const handle = (e) => {
    const r = barRef.current.getBoundingClientRect();
    const x = clamp(e.clientX - r.left, 0, r.width);
    onSeek((x / r.width) * duration);
  };
  return (
    <div className="relative select-none">
      <div ref={barRef} onMouseDown={(e) => { handle(e); const up = () => window.removeEventListener("mousemove", handle); window.addEventListener("mousemove", handle); window.addEventListener("mouseup", () => { up(); window.removeEventListener("mouseup", up); }, { once: true }); }}
        className="h-3 bg-black border border-metal cursor-pointer relative overflow-hidden" data-testid="cctv-scrubber">
        <div className="absolute inset-y-0 right-0 bg-gradient-to-r from-yellow-800/40 to-yellow-600/20" style={{ width: `${(seconds / duration) * 100}%` }} />
        {events.map((ev, i) => {
          const p = (ev.t / duration) * 100;
          const color = ev.tag === "critical" ? "#EF4444" : ev.tag === "suspicious" ? "#F59E0B" : "#22D3EE";
          return (
            <button key={i} onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
              data-testid={`cctv-event-${i}`}
              className="absolute top-0 bottom-0 w-1 hover:w-1.5"
              style={{ right: `${100 - p}%`, background: color, boxShadow: `0 0 6px ${color}` }} title={`${ev.time} · ${ev.label}`} />
          );
        })}
        <div className="absolute inset-y-0 w-0.5 bg-gold" style={{ right: `${100 - (seconds / duration) * 100}%`, boxShadow: "0 0 8px rgba(240,217,124,0.9)" }} />
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 font-mono-ar mt-1 tabular-nums">
        <span>{secondsToTC(seconds)}</span>
        <span>{secondsToTC(duration)}</span>
      </div>
    </div>
  );
}

/** Main CCTV Control Room component */
export function CCTVControlRoom({ open, onClose, caseId, suspects }) {
  const { isOwner, hasPerm } = useAuth();
  const canManageCctv = isOwner || hasPerm("VIEW_CCTV") || hasPerm("MANAGE_CCTV_MEDIA");
  const cctvPermissionMessage = "يتطلب صلاحية مشاهدة الكاميرات للتحقيق";
  const [feeds, setFeeds] = useState([]);
  const [camIdx, setCamIdx] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [fullscreen, setFullscreen] = useState(false);
  const [marker, setMarker] = useState(null);
  const [markMode, setMarkMode] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [saved, setSaved] = useState([]); // captured CCTV evidence list
  const [duplicateWarn, setDuplicateWarn] = useState(null);
  const [log, setLog] = useState([]);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [inspectZoom, setInspectZoom] = useState(2);
  const [inspectPan, setInspectPan] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const containerRef = useRef(null);

  const camera = feeds[camIdx];
  const currentEvent = useMemo(() => {
    if (!camera) return null;
    const evs = [...camera.events].sort((a, b) => a.t - b.t);
    let cur = evs[0];
    for (const e of evs) { if (e.t <= seconds) cur = e; else break; }
    return cur || null;
  }, [camera, seconds]);

  const loadFeeds = useCallback(async () => {
    try { const { data } = await api.get(`/cctv/${caseId}`); setFeeds(data || []); } catch {}
  }, [caseId]);
  const loadSaved = useCallback(async () => {
    try { const { data } = await api.get(`/session/cctv-evidence/${caseId}`); setSaved(data || []); } catch {}
  }, [caseId]);
  const loadLog = useCallback(async () => {
    try { const { data } = await api.get(`/session/cctv-log/${caseId}`); setLog(data || []); } catch {}
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    loadFeeds(); loadSaved(); loadLog();
    sfx.cctvOpen();
    setSeconds(0); setPlaying(true); setZoom(1); setPan({ x: 0, y: 0 }); setMarker(null); setCamIdx(0);
  }, [open, loadFeeds, loadSaved, loadLog]);

  // Play loop
  useEffect(() => {
    if (!open || !playing || !camera) return;
    let last = performance.now();
    const step = (now) => {
      const dt = (now - last) / 1000; last = now;
      setSeconds(s => {
        const nx = s + dt * speed;
        if (nx >= camera.duration) { setPlaying(false); return camera.duration; }
        return nx;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, playing, camera?.id, speed, camera?.duration]);

  // ESC-only close (per P8 acceptance: NO application hotkeys — no Space/Arrow/+/-).
  // Escape is retained because it is standard/safe modal behavior.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { if (fullscreen) setFullscreen(false); else onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, fullscreen, onClose]);

  const switchCam = (idx) => {
    setCamIdx(idx); setSeconds(0); setZoom(1); setPan({ x: 0, y: 0 }); setMarker(null);
    sfx.cctvSwitch();
    api.post("/session/cctv-log", { case_id: caseId, message: `تم فتح الكاميرا ${feeds[idx]?.id} — ${feeds[idx]?.name}` }).then(() => loadLog()).catch(() => {});
  };

  const jumpToEvent = (ev) => { setSeconds(ev.t); setPlaying(false); sfx.click(); };

  const onFrameMouseDown = (e) => {
    if (!markMode || !playing === false) {}
    if (!markMode) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    setDrawStart({ x, y });
    setMarker({ x, y, w: 0, h: 0 });
  };
  const onFrameMouseMove = (e) => {
    if (!markMode || !drawStart) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    setMarker({ x: Math.min(drawStart.x, x), y: Math.min(drawStart.y, y), w: Math.abs(x - drawStart.x), h: Math.abs(y - drawStart.y) });
  };
  const onFrameMouseUp = () => { setDrawStart(null); setMarkMode(false); };

  const captureFrame = async (force = false) => {
    if (!canManageCctv) {
      toast.error(cctvPermissionMessage);
      return;
    }
    if (!camera) return;
    setPlaying(false);
    const body = {
      case_id: caseId,
      camera_id: camera.id, camera_name: camera.name, camera_location: camera.location,
      seconds, timestamp: currentEvent?.time || secondsToTC(seconds),
      event_label: currentEvent?.label || "لقطة يدوية",
      suspect_id: currentEvent?.suspect_id || null,
      marker: marker || null, force_duplicate: force,
    };
    try {
      const { data } = await api.post("/session/cctv-evidence", body);
      if (data.duplicate) {
        setDuplicateWarn({ id: data.existing_id });
      } else {
        sfx.cctvCapture();
        setDuplicateWarn(null);
        loadSaved(); loadLog();
        toast.success(`تم حفظ اللقطة كدليل (${data.evidence?.id || ""})`);
      }
    } catch (e) {
      if (e?.response?.status === 403) toast.error(cctvPermissionMessage);
      else toast.error("تعذر حفظ اللقطة حالياً");
    }
  };

  const toggleReveal = async (ev) => {
    try { await api.patch(`/session/cctv-evidence/${caseId}/${ev.id}`, { revealed: !ev.revealed }); sfx.evidenceReveal(); loadSaved(); loadLog(); } catch {}
  };
  const togglePin = async (ev) => {
    try { await api.patch(`/session/cctv-evidence/${caseId}/${ev.id}`, { pinned: !ev.pinned }); sfx.click(); loadSaved(); } catch {}
  };
  const deleteSaved = async (ev) => {
    try { await api.delete(`/session/cctv-evidence/${caseId}/${ev.id}`); sfx.evidenceUndo(); loadSaved(); } catch {}
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        ref={containerRef} dir="rtl"
        className={`fixed inset-0 z-[999] bg-black text-white flex flex-col ${fullscreen ? "" : "p-4 md:p-6"}`}
        data-testid="cctv-control-room">
        {/* HEADER */}
        {!fullscreen && (
          <div className="flex items-center justify-between mb-3 shrink-0">
            <div className="flex items-center gap-3">
              <CameraIcon size={26} className="text-gold" strokeWidth={1.2} />
              <div>
                <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">CCTV CONTROL ROOM</div>
                <div className="font-cairo text-2xl">غرفة كاميرات المراقبة</div>
              </div>
              <div className="mr-4 flex items-center gap-2 text-[10px] font-mono-ar text-red-400 bg-red-950/40 border border-red-900/60 px-2 py-1">
                <Radio size={11} className="animate-pulse" /> LIVE STREAM · SURVEILLANCE
              </div>
            </div>
            <button onClick={onClose} data-testid="cctv-close" className="w-10 h-10 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={18} /></button>
          </div>
        )}

        {/* MAIN LAYOUT */}
        <div className={`flex-1 grid gap-3 min-h-0 overflow-hidden ${fullscreen ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-[220px_1fr_320px]"}`}>
          {/* LEFT: Camera list */}
          {!fullscreen && (
            <div className="panel border-metal overflow-y-auto" data-testid="cctv-camera-list">
              <div className="px-3 py-2 border-b border-metal text-[10px] tracking-widest text-gold font-mono-ar">CAMERAS · {feeds.length}</div>
              <div className="p-2 space-y-1">
                {feeds.map((f, i) => (
                  <button key={f.id} onClick={() => switchCam(i)}
                    data-testid={`cctv-cam-${f.id}`}
                    className={`w-full text-right p-2 border rounded-sm text-xs transition-colors ${i === camIdx ? "border-gold bg-[rgba(197,160,89,0.1)] text-gold" : "border-metal text-zinc-300 hover:border-gold/50"}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-red-500 text-[9px] font-mono-ar">● REC</span>
                      <span className="font-mono-ar text-[10px]">{f.id}</span>
                    </div>
                    <div className="mt-1 font-cairo">{f.name}</div>
                    <div className="text-[9px] opacity-70 mt-0.5">{f.location}</div>
                    <div className="text-[9px] mt-1 flex justify-between opacity-70">
                      <span>{f.resolution}</span><span className="font-mono-ar">{secondsToTC(f.duration)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CENTER: Viewer + controls */}
          <div className="flex flex-col gap-2 min-h-0">
            <div className="relative bg-black border border-metal flex-1 min-h-0 overflow-hidden"
              onMouseDown={onFrameMouseDown} onMouseMove={onFrameMouseMove} onMouseUp={onFrameMouseUp}>
              {camera ? (
                <CCTVFrameCanvas camera={camera} event={currentEvent} seconds={seconds} suspects={suspects}
                  zoom={zoom} pan={pan} marker={marker} paused={!playing} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">لا توجد كاميرات لهذه القضية.</div>
              )}
              {/* HUD overlays */}
              <div className="absolute top-2 right-3 left-3 flex justify-between text-[11px] font-mono-ar pointer-events-none">
                <div className="bg-black/70 border border-red-800/60 px-2 py-0.5 flex items-center gap-1 text-red-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> REC {camera?.id}
                </div>
                <div className="bg-black/70 border border-cyan-800/50 px-2 py-0.5 text-cyan-400">
                  {camera?.resolution} · SIGNAL <span className="text-emerald-400">ONLINE</span>
                </div>
              </div>
              <div className="absolute bottom-2 right-3 left-3 flex justify-between text-[11px] font-mono-ar pointer-events-none">
                <div className="bg-black/70 border border-metal px-2 py-0.5 text-zinc-300">{camera?.location}</div>
                <div className="bg-black/70 border border-gold px-2 py-0.5 text-gold tabular-nums">
                  {currentEvent?.time || "--:--"} · <span className="text-white">{secondsToTC(seconds)}</span>
                </div>
              </div>
              {/* Current event caption */}
              {currentEvent && (
                <div className="absolute bottom-10 right-3 left-3 pointer-events-none">
                  <div className={`inline-block bg-black/80 border px-3 py-1 text-xs ${currentEvent.tag === "critical" ? "border-red-700 text-red-300" : currentEvent.tag === "suspicious" ? "border-amber-700 text-amber-300" : "border-cyan-800 text-cyan-300"}`}>
                    {currentEvent.label}
                  </div>
                </div>
              )}
              {markMode && (
                <div className="absolute inset-0 bg-gold/5 pointer-events-none">
                  <div className="absolute top-3 left-3 text-[10px] text-gold bg-black/80 border border-gold px-2 py-1 font-mono-ar pointer-events-none">حدد المنطقة على الشاشة</div>
                </div>
              )}
            </div>

            {/* Scrubber */}
            <Scrubber duration={camera?.duration || 1} seconds={seconds} events={camera?.events || []}
              onSeek={(s) => { setSeconds(s); setPlaying(false); }} onEventClick={jumpToEvent} />

            {/* Playback + zoom controls */}
            <div className="panel border-metal p-2 flex flex-wrap gap-2 items-center justify-between">
              <div className="flex items-center gap-1">
                <CtlBtn onClick={() => setSeconds(s => Math.max(0, s - 10))} testid="cctv-back10" tip="رجوع 10 ثوانٍ"><SkipBack size={14} /></CtlBtn>
                <CtlBtn onClick={() => setPlaying(p => !p)} primary testid="cctv-playpause" tip={playing ? "إيقاف مؤقت" : "تشغيل"}>{playing ? <Pause size={16} /> : <Play size={16} />}</CtlBtn>
                <CtlBtn onClick={() => setSeconds(s => Math.min(camera?.duration || 0, s + 10))} testid="cctv-fwd10" tip="تقديم 10 ثوانٍ"><SkipForward size={14} /></CtlBtn>
                <CtlBtn onClick={() => { setSeconds(0); setPlaying(true); }} testid="cctv-replay" tip="إعادة"><RotateCw size={14} /></CtlBtn>
                <div className="mx-2 flex items-center gap-1 text-[11px] text-zinc-400 font-mono-ar">
                  {[1, 2, 4].map(v => (
                    <button key={v} onClick={() => setSpeed(v)} data-testid={`cctv-speed-${v}`}
                      className={`px-2 py-0.5 border text-[10px] ${speed === v ? "border-gold text-gold" : "border-metal hover:border-gold/50"}`}>{v}×</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <CtlBtn onClick={() => { setZoom(z => Math.max(1, z - 0.5)); if (zoom <= 1) setPan({ x: 0, y: 0 }); }} testid="cctv-zoom-out" tip="تصغير"><ZoomOut size={14} /></CtlBtn>
                <div className="text-[10px] text-gold font-mono-ar min-w-[44px] text-center">{Math.round(zoom * 100)}%</div>
                <CtlBtn onClick={() => setZoom(z => Math.min(4, z + 0.5))} testid="cctv-zoom-in" tip="تكبير"><ZoomIn size={14} /></CtlBtn>
                {zoom > 1 && (
                  <div className="flex items-center gap-0.5 mx-1 text-zinc-500 text-[10px]">
                    <button onClick={() => setPan(p => ({ ...p, x: p.x + 0.15 }))} className="px-1 border border-metal hover:border-gold">→</button>
                    <button onClick={() => setPan(p => ({ ...p, x: p.x - 0.15 }))} className="px-1 border border-metal hover:border-gold">←</button>
                    <button onClick={() => setPan(p => ({ ...p, y: p.y + 0.15 }))} className="px-1 border border-metal hover:border-gold">↓</button>
                    <button onClick={() => setPan(p => ({ ...p, y: p.y - 0.15 }))} className="px-1 border border-metal hover:border-gold">↑</button>
                  </div>
                )}
                <CtlBtn onClick={() => setMarkMode(m => !m)} active={markMode} testid="cctv-mark-toggle" tip="تحديد موضع مهم"><Square size={14} /></CtlBtn>
                {marker && <button onClick={() => setMarker(null)} className="text-[10px] text-red-400 border border-red-900 px-2 py-1 hover:bg-red-950/30" data-testid="cctv-mark-clear">إزالة التحديد</button>}
                {marker && marker.w > 0.03 && marker.h > 0.03 && (
                  <button onClick={() => { setInspectOpen(true); setInspectZoom(2); setInspectPan({ x: 0, y: 0 }); setPlaying(false); sfx.audioEnhance(); }}
                    className="text-[10px] text-gold border border-gold px-2 py-1 hover:bg-[rgba(197,160,89,0.1)] flex items-center gap-1" data-testid="cctv-inspect">
                    <Search size={11} /> تكبير المنطقة
                  </button>
                )}
                <CtlBtn onClick={() => setFullscreen(f => !f)} testid="cctv-fullscreen" tip={fullscreen ? "خروج ملء الشاشة" : "ملء الشاشة"}>{fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</CtlBtn>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => captureFrame(false)} data-testid="cctv-capture"
                  disabled={!canManageCctv} title={!canManageCctv ? cctvPermissionMessage : "حفظ اللقطة كدليل"}
                  className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"><Save size={12} /> حفظ اللقطة كدليل</button>
                {!canManageCctv && <span className="text-[10px] text-amber-300/80 font-cairo">يتطلب صلاحية مشاهدة الكاميرات</span>}
              </div>
            </div>

            {/* Duplicate warning */}
            {duplicateWarn && (
              <div className="panel border-yellow-700 p-3 flex items-start gap-3 text-xs" data-testid="cctv-dup-warn">
                <AlertCircle className="text-yellow-500 shrink-0" size={18} />
                <div className="flex-1">
                  <div className="text-yellow-400 font-cairo">هذه اللقطة محفوظة مسبقاً كدليل</div>
                  <div className="text-zinc-400 text-[11px] mt-1">الدليل الموجود: <span className="text-gold font-mono-ar">{duplicateWarn.id}</span></div>
                </div>
                <button onClick={() => { setDuplicateWarn(null); captureFrame(true); }} className="btn-ghost text-[11px]" data-testid="cctv-force-save">حفظ نسخة أخرى</button>
                <button onClick={() => setDuplicateWarn(null)} className="text-zinc-500 hover:text-white"><X size={14} /></button>
              </div>
            )}
          </div>

          {/* RIGHT: Saved evidence + activity log */}
          {!fullscreen && (
            <div className="flex flex-col gap-2 min-h-0">
              <div className="panel border-metal flex-1 min-h-0 flex flex-col">
                <div className="px-3 py-2 border-b border-metal flex items-center justify-between">
                  <div className="text-[10px] tracking-widest text-gold font-mono-ar">CCTV EVIDENCE · {saved.length}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {saved.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-6">لا توجد لقطات محفوظة بعد.</div>}
                  {saved.map(ev => {
                    const sus = suspects.find(s => s.id === ev.suspect_id);
                    return (
                      <div key={ev.id} className={`border rounded-sm p-2 text-xs ${ev.pinned ? "border-gold bg-[rgba(197,160,89,0.08)]" : "border-metal"}`} data-testid={`cctv-saved-${ev.id}`}>
                        <div className="flex justify-between items-start mb-1">
                          <span className="evidence-tag">{ev.id}</span>
                          <div className="flex gap-1">
                            <button title="تثبيت كدليل مهم" onClick={() => togglePin(ev)} data-testid={`cctv-pin-${ev.id}`} className={ev.pinned ? "text-gold" : "text-zinc-600 hover:text-gold"}><Pin size={12} /></button>
                            <button title={ev.revealed ? "إخفاء" : "كشف للمشاهدين"} onClick={() => toggleReveal(ev)} data-testid={`cctv-reveal-${ev.id}`} className={ev.revealed ? "text-emerald-400" : "text-zinc-600 hover:text-white"}>{ev.revealed ? <Eye size={12} /> : <EyeOff size={12} />}</button>
                            <button title="حذف" onClick={() => deleteSaved(ev)} data-testid={`cctv-del-${ev.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={12} /></button>
                          </div>
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono-ar">{ev.camera_id} · {ev.timestamp}</div>
                        <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{ev.camera_name}</div>
                        <div className="text-[11px] text-white mt-1 line-clamp-2">{ev.event_label}</div>
                        {sus && <div className="mt-1 flex items-center gap-1.5">
                          <img src={assetUrl(sus.portrait)} alt="" className="w-6 h-6 object-cover border border-metal" />
                          <span className="text-[10px] text-gold font-cairo">{sus.name}</span>
                        </div>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="panel border-metal max-h-[180px] flex flex-col">
                <div className="px-3 py-2 border-b border-metal text-[10px] tracking-widest text-gold font-mono-ar">ACTIVITY LOG</div>
                <div className="p-2 space-y-1 overflow-y-auto text-[10px]" data-testid="cctv-log">
                  {log.length === 0 && <div className="text-zinc-600 text-center py-3">لا توجد أحداث بعد.</div>}
                  {log.map((l, i) => (
                    <div key={i} className="text-zinc-300 flex gap-2 leading-tight">
                      <span className="text-zinc-600 font-mono-ar shrink-0">{new Date(l.at).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                      <span className="flex-1">{l.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {fullscreen && (
          <button onClick={() => setFullscreen(false)} className="fixed top-4 left-4 z-[70] w-10 h-10 border border-gold text-gold bg-black/80 hover:bg-gold/10 flex items-center justify-center" data-testid="cctv-exit-fullscreen">
            <Minimize2 size={16} />
          </button>
        )}

        {/* FORENSIC INSPECTION PANEL — Freeze-Frame Zoom */}
        {inspectOpen && marker && camera && (
          <InspectionPanel
            camera={camera} event={currentEvent} suspects={suspects} marker={marker}
            seconds={seconds} caseId={caseId} zoom={inspectZoom} pan={inspectPan}
            setZoom={setInspectZoom} setPan={setInspectPan}
            onClose={() => setInspectOpen(false)}
            canManageCctv={canManageCctv}
            cctvPermissionMessage={cctvPermissionMessage}
            onSaveAsEvidence={async (note) => {
              if (!canManageCctv) {
                toast.error(cctvPermissionMessage);
                return;
              }
              const body = {
                case_id: caseId, camera_id: camera.id, camera_name: camera.name,
                camera_location: camera.location, seconds, timestamp: currentEvent?.time || secondsToTC(seconds),
                event_label: `[تكبير] ${currentEvent?.label || "لقطة"}`,
                suspect_id: currentEvent?.suspect_id || null,
                marker, force_duplicate: true, note: note || "لقطة تكبير جنائي",
              };
              try {
                const { data } = await api.post("/session/cctv-evidence", body);
                if (!data.duplicate) {
                  sfx.cctvCapture();
                  loadSaved();
                  loadLog();
                  toast.success(`تم حفظ التكبير الجنائي كدليل (${data.evidence?.id || ""})`);
                }
              } catch (e) {
                if (e?.response?.status === 403) toast.error(cctvPermissionMessage);
                else toast.error("تعذر حفظ التكبير حالياً");
                return;
              }
              setInspectOpen(false);
            }}
          />
        )}

        <div className="text-center pt-2 text-[9px] text-zinc-600 tracking-[0.3em] font-cairo">OK FBI · SURVEILLANCE · BY OK</div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function CtlBtn({ children, onClick, primary, active, testid, tip }) {
  return (
    <button onClick={onClick} data-testid={testid} title={tip}
      className={`w-9 h-9 flex items-center justify-center border transition-colors ${primary ? "border-gold text-gold hover:bg-[rgba(197,160,89,0.12)]" : active ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-300 hover:border-gold/60 hover:text-gold"}`}>
      {children}
    </button>
  );
}

/** Forensic inspection panel — freeze-frame zoom of the marked area of interest.
 *  Uses the same base scenery composition, but zooms into the marker rectangle
 *  with additional cinematic enhancement (contrast, sharper edges). */
function InspectionPanel({ camera, event, suspects, marker, seconds, caseId, zoom, pan, setZoom, setPan, onClose, onSaveAsEvidence, canManageCctv, cctvPermissionMessage }) {
  const [note, setNote] = useState("");
  const canvasRef = useRef(null);
  const suspect = event?.suspect_id ? suspects.find(s => s.id === event.suspect_id) : null;
  const [imgTick, setImgTick] = useState(0);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!suspect) { imgRef.current = null; setImgTick(t => t + 1); return; }
    const img = new Image();
    img.onload = () => { imgRef.current = img; setImgTick(t => t + 1); };
    img.onerror = () => { imgRef.current = null; setImgTick(t => t + 1); };
    img.src = assetUrl(suspect.portrait);
  }, [suspect?.id]); // eslint-disable-line

  useEffect(() => {
    const cvs = canvasRef.current; if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width, H = cvs.height;
    ctx.clearRect(0, 0, W, H);
    // Compute source rect in a virtual 1280x720 base canvas
    const baseW = 1280, baseH = 720;
    const sx = marker.x * baseW, sy = marker.y * baseH;
    const sw = marker.w * baseW, sh = marker.h * baseH;
    // Draw scenery inside the source rect area
    // 1. Fill background
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "#0A0C10"); grad.addColorStop(1, "#000");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    // 2. Portrait composited if this marker overlaps portrait area
    const px = 0.5 - (baseH * 0.55 / baseW) / 2; // 0.5 - 0.275/2 - approximation
    if (imgRef.current) {
      // Portrait region in the base canvas
      const portW = baseH * 0.55, portH = portW * 1.28;
      const portX = baseW * 0.5 - portW / 2, portY = baseH * 0.20;
      // Intersection with source rect
      const ix1 = Math.max(sx, portX), iy1 = Math.max(sy, portY);
      const ix2 = Math.min(sx + sw, portX + portW), iy2 = Math.min(sy + sh, portY + portH);
      if (ix2 > ix1 && iy2 > iy1) {
        const srcX = ((ix1 - portX) / portW) * imgRef.current.width;
        const srcY = ((iy1 - portY) / portH) * imgRef.current.height;
        const srcW = ((ix2 - ix1) / portW) * imgRef.current.width;
        const srcH = ((iy2 - iy1) / portH) * imgRef.current.height;
        // Destination in inspection canvas, scaled by zoom + pan
        const scaleX = W / sw, scaleY = H / sh;
        const scale = Math.min(scaleX, scaleY) * zoom;
        const dw = srcW * (portW / imgRef.current.width) * scale;
        const dh = srcH * (portH / imgRef.current.height) * scale;
        const dx = ((ix1 - sx) * scaleX * zoom) + pan.x * W - (zoom - 1) * W / 2;
        const dy = ((iy1 - sy) * scaleY * zoom) + pan.y * H - (zoom - 1) * H / 2;
        ctx.save();
        ctx.filter = "contrast(1.15) brightness(1.05)";
        ctx.drawImage(imgRef.current, srcX, srcY, srcW, srcH, dx, dy, dw, dh);
        ctx.restore();
      }
    }
    // Overlay: scanlines + heavy vignette
    ctx.globalAlpha = 0.08;
    for (let y = 0; y < H; y += 3) {
      ctx.fillStyle = y % 6 === 0 ? "#000" : "rgba(180,220,180,0.35)";
      ctx.fillRect(0, y, W, 1);
    }
    ctx.globalAlpha = 1;
    // grid
    ctx.strokeStyle = "rgba(197,160,89,0.18)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo(0, (H / 4) * i); ctx.lineTo(W, (H / 4) * i); ctx.stroke();
      ctx.beginPath(); ctx.moveTo((W / 4) * i, 0); ctx.lineTo((W / 4) * i, H); ctx.stroke();
    }
    // Vignette
    const vg = ctx.createRadialGradient(W/2, H/2, W*0.25, W/2, H/2, W*0.7);
    vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,0,0.7)");
    ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);
    // Watermark
    ctx.fillStyle = "rgba(197,160,89,0.6)";
    ctx.font = "12px monospace";
    ctx.fillText(`ENHANCED · ${camera.id} · x${zoom.toFixed(1)}`, 12, 20);
  }, [camera.id, marker.x, marker.y, marker.w, marker.h, zoom, pan.x, pan.y, imgTick]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4" data-testid="cctv-inspection-panel" dir="rtl">
      <div className="panel border-gold w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-metal shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">FORENSIC INSPECTION · FREEZE FRAME</div>
            <div className="font-cairo text-xl text-white mt-1">تكبير جنائي للمنطقة المحددة</div>
          </div>
          <button onClick={onClose} data-testid="cctv-inspect-close" className="w-9 h-9 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3 p-3 flex-1 min-h-0">
          <div className="relative bg-black border border-gold/60 overflow-hidden">
            <canvas ref={canvasRef} width={1280} height={720} className="w-full h-full object-contain" />
            <div className="absolute top-2 right-3 left-3 flex justify-between text-[10px] font-mono-ar pointer-events-none">
              <div className="bg-black/70 border border-red-900 px-2 py-0.5 text-red-400">● ENHANCED</div>
              <div className="bg-black/70 border border-gold/60 px-2 py-0.5 text-gold">{camera.id} · {event?.time || secondsToTC(seconds)}</div>
            </div>
            <div className="absolute bottom-2 right-3 left-3 flex justify-between text-[10px] font-mono-ar pointer-events-none">
              <div className="bg-black/70 border border-metal px-2 py-0.5 text-zinc-300">{camera.location}</div>
              <div className="bg-black/70 border border-metal px-2 py-0.5 text-zinc-300">CASE {caseId?.toUpperCase()} · x{zoom.toFixed(1)}</div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="panel border-metal p-3 text-xs">
              <div className="label-caps mb-2">تفاصيل المصدر</div>
              <div className="space-y-1 text-[11px] text-zinc-300">
                <div className="flex justify-between"><span className="text-zinc-500">الكاميرا:</span><span className="text-white font-mono-ar">{camera.id}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">الموقع:</span><span className="text-white">{camera.location}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">الوقت:</span><span className="text-gold font-mono-ar">{event?.time || secondsToTC(seconds)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">الثانية:</span><span className="text-white font-mono-ar">{seconds.toFixed(1)}s</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">القضية:</span><span className="text-white font-mono-ar">{caseId?.toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">منطقة:</span><span className="text-white font-mono-ar">{Math.round(marker.w * 100)}×{Math.round(marker.h * 100)}%</span></div>
              </div>
            </div>
            <div className="panel border-metal p-3 text-xs space-y-2">
              <div className="label-caps">التكبير والتحريك</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setZoom(Math.max(1, zoom - 0.5))} data-testid="cctv-inspect-zoom-out" className="w-8 h-8 border border-metal hover:border-gold text-zinc-300 hover:text-gold flex items-center justify-center"><ZoomOut size={12} /></button>
                <div className="flex-1 text-center text-gold font-mono-ar">x{zoom.toFixed(1)}</div>
                <button onClick={() => setZoom(Math.min(6, zoom + 0.5))} data-testid="cctv-inspect-zoom-in" className="w-8 h-8 border border-metal hover:border-gold text-zinc-300 hover:text-gold flex items-center justify-center"><ZoomIn size={12} /></button>
              </div>
              <div className="grid grid-cols-3 gap-1 text-[10px]">
                <div></div>
                <button onClick={() => setPan(p => ({ ...p, y: p.y + 0.1 }))} className="border border-metal p-1 hover:border-gold">↑</button>
                <div></div>
                <button onClick={() => setPan(p => ({ ...p, x: p.x + 0.1 }))} className="border border-metal p-1 hover:border-gold">←</button>
                <button onClick={() => setPan({ x: 0, y: 0 })} className="border border-metal p-1 hover:border-gold text-[9px]">مركز</button>
                <button onClick={() => setPan(p => ({ ...p, x: p.x - 0.1 }))} className="border border-metal p-1 hover:border-gold">→</button>
                <div></div>
                <button onClick={() => setPan(p => ({ ...p, y: p.y - 0.1 }))} className="border border-metal p-1 hover:border-gold">↓</button>
                <div></div>
              </div>
            </div>
            <div className="panel border-metal p-3 text-xs">
              <div className="label-caps mb-2">ملاحظة المحقق</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="cctv-inspect-note" rows={3} placeholder="مثال: يظهر شيء يشبه المفتاح في اليد اليمنى..."
                className="w-full bg-black/50 border border-metal p-2 text-xs text-white outline-none focus:border-gold resize-none" />
            </div>
            <button onClick={() => onSaveAsEvidence(note)} data-testid="cctv-inspect-save"
              disabled={!canManageCctv} title={!canManageCctv ? cctvPermissionMessage : "حفظ التكبير كدليل"}
              className="btn-primary py-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"><Save size={12} /> حفظ التكبير كدليل</button>
            {!canManageCctv && <div className="text-[10px] text-amber-300/80 font-cairo text-center">يتطلب صلاحية مشاهدة الكاميرات</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
